import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  FiClock,
  FiFileText,
  FiInfo,
  FiPlus,
  FiPercent,
  FiRefreshCw,
  FiSave,
  FiSettings,
  FiSliders,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  fetchBillingConfigurationThunk,
  updateSubscriptionBillingConfigThunk,
  updateBillingSchedulerThunk,
  updateInvoiceBillingConfigThunk,
  updateManualTaxBillingConfigThunk,
  addBillingSettingThunk,
  updateBillingSettingThunk,
  clearBillingConfigurationError,
} from "../../../store/slices/panchayathBillingSlice";

const MANUAL_CATEGORIES = [
  { key: "installation", label: "Installation" },
  { key: "training", label: "Training" },
  { key: "printer", label: "Printer" },
  { key: "other", label: "Other" },
];

const EMPTY_CONFIGURATION = {
  subscription: {
    gst_percentage: "",
  },

  scheduler: {
    subscription_cron_time: "",
  },

  invoice: {
    invoice_prefix: "",
  },

  manual_tax: {
    installation: {
      category: "installation",
      tax_type: "GST",
      tax_mode: "percentage",
      tax_value: "",
    },

    training: {
      category: "training",
      tax_type: "GST",
      tax_mode: "percentage",
      tax_value: "",
    },

    printer: {
      category: "printer",
      tax_type: "GST",
      tax_mode: "percentage",
      tax_value: "",
    },

    other: {
      category: "other",
      tax_type: "Non-GST",
      tax_mode: "percentage",
      tax_value: 0,
    },
  },

  additional_settings: [],
};

const EMPTY_NEW_SETTING = {
  setting_key: "",
  setting_value: "",
  description: "",
  is_active: true,
};

const cloneConfiguration = (data = {}) => {
  const manualTax =
    data?.manual_tax || {};

  const normalizedManualTax = {};

  MANUAL_CATEGORIES.forEach(({ key }) => {
    const source =
      manualTax?.[key] || {};

    normalizedManualTax[key] = {
      category: key,

      tax_type:
        source?.tax_type === "Non-GST"
          ? "Non-GST"
          : "GST",

      tax_mode:
        source?.tax_mode === "amount"
          ? "amount"
          : "percentage",

      tax_value:
        source?.tax_type === "Non-GST"
          ? 0
          : source?.tax_value ?? "",
    };
  });

  return {
    subscription: {
      gst_percentage:
        data?.subscription?.gst_percentage ?? "",
    },

    scheduler: {
      subscription_cron_time:
        data?.scheduler?.subscription_cron_time ?? "",
    },

    invoice: {
      invoice_prefix:
        data?.invoice?.invoice_prefix ?? "",
    },

    manual_tax:
      normalizedManualTax,

    additional_settings:
      Array.isArray(
        data?.additional_settings
      )
        ? data.additional_settings.map(
            (item) => ({
              ...item,
              setting_id:
                Number(item.setting_id),
              setting_value:
                item.setting_value ?? "",
              description:
                item.description ?? "",
              is_active:
                Boolean(item.is_active),
            })
          )
        : [],
  };
};

const isValidTime = (value) => {
  const text =
    String(value || "").trim();

  if (!/^\d{2}:\d{2}$/.test(text)) {
    return false;
  }

  const [hour, minute] =
    text.split(":").map(Number);

  return (
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59
  );
};

const isValidPrefix = (value) => {
  const text =
    String(value || "").trim();

  return (
    text.length >= 1 &&
    text.length <= 20 &&
    /^[A-Za-z0-9_-]+$/.test(text)
  );
};

const isValidSettingKey = (value) =>
  /^[a-z][a-z0-9_]{1,99}$/.test(
    String(value || "").trim()
  );

const BillingConfigurationPage = () => {
  const dispatch = useDispatch();

  const {
    billingConfigurationLoading,
    savingSubscriptionConfig,
    savingSchedulerConfig,
    savingInvoiceConfig,
    savingManualTaxConfig,
    addingBillingSetting,
    savingAdditionalSettings,
    billingConfigurationError,
  } = useSelector(
    (state) =>
      state.panchayathBilling
  );

  const [
    config,
    setConfig,
  ] = useState(
    EMPTY_CONFIGURATION
  );

  const [
    originalConfig,
    setOriginalConfig,
  ] = useState(
    EMPTY_CONFIGURATION
  );

  const [
    feedback,
    setFeedback,
  ] = useState(null);

  const [
    showAddSetting,
    setShowAddSetting,
  ] = useState(false);

  const [
    newSetting,
    setNewSetting,
  ] = useState(
    EMPTY_NEW_SETTING
  );

  const showFeedback =
    useCallback(
      (type, message) => {
        setFeedback({
          type,
          message,
        });
      },
      []
    );

  const loadConfiguration =
    useCallback(async () => {
      setFeedback(null);

      const result =
        await dispatch(
          fetchBillingConfigurationThunk()
        );

      if (
        fetchBillingConfigurationThunk
          .fulfilled.match(result)
      ) {
        const normalized =
          cloneConfiguration(
            result.payload
          );

        setConfig(normalized);
        setOriginalConfig(
          normalized
        );
      } else {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to load billing configuration"
        );
      }
    }, [
      dispatch,
      showFeedback,
    ]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  useEffect(() => {
    if (
      billingConfigurationError
    ) {
      showFeedback(
        "danger",
        billingConfigurationError
      );

      dispatch(
        clearBillingConfigurationError()
      );
    }
  }, [
    billingConfigurationError,
    dispatch,
    showFeedback,
  ]);

  const updateSection = (
    section,
    field,
    value
  ) => {
    setConfig((prev) => ({
      ...prev,

      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateManualTax = (
    category,
    field,
    value
  ) => {
    setConfig((prev) => {
      const current =
        prev.manual_tax?.[
          category
        ] || {};

      let next = {
        ...current,
        [field]: value,
      };

      if (
        field === "tax_type" &&
        value === "Non-GST"
      ) {
        next = {
          ...next,
          tax_mode:
            "percentage",
          tax_value: 0,
        };
      }

      if (
        field === "tax_type" &&
        value === "GST" &&
        Number(next.tax_value) === 0
      ) {
        next.tax_value = "";
      }

      return {
        ...prev,

        manual_tax: {
          ...prev.manual_tax,
          [category]: next,
        },
      };
    });
  };

  const updateAdditionalLocal = (
    settingId,
    field,
    value
  ) => {
    setConfig((prev) => ({
      ...prev,

      additional_settings:
        prev.additional_settings.map(
          (item) =>
            Number(item.setting_id) ===
            Number(settingId)
              ? {
                  ...item,
                  [field]: value,
                }
              : item
        ),
    }));
  };

  const subscriptionDirty =
    useMemo(
      () =>
        String(
          config.subscription
            .gst_percentage
        ) !==
        String(
          originalConfig
            .subscription
            .gst_percentage
        ),
      [
        config.subscription
          .gst_percentage,
        originalConfig.subscription
          .gst_percentage,
      ]
    );

  const schedulerDirty =
    useMemo(
      () =>
        String(
          config.scheduler
            .subscription_cron_time
        ) !==
        String(
          originalConfig
            .scheduler
            .subscription_cron_time
        ),
      [
        config.scheduler
          .subscription_cron_time,
        originalConfig.scheduler
          .subscription_cron_time,
      ]
    );

  const invoiceDirty =
    useMemo(
      () =>
        String(
          config.invoice
            .invoice_prefix
        ) !==
        String(
          originalConfig.invoice
            .invoice_prefix
        ),
      [
        config.invoice
          .invoice_prefix,
        originalConfig.invoice
          .invoice_prefix,
      ]
    );

  const isManualDirty = (
    category
  ) => {
    const current =
      config.manual_tax?.[
        category
      ];

    const original =
      originalConfig
        .manual_tax?.[
        category
      ];

    return (
      String(
        current?.tax_type
      ) !==
        String(
          original?.tax_type
        ) ||
      String(
        current?.tax_mode
      ) !==
        String(
          original?.tax_mode
        ) ||
      String(
        current?.tax_value
      ) !==
        String(
          original?.tax_value
        )
    );
  };

  const isAdditionalDirty = (
    settingId
  ) => {
    const current =
      config.additional_settings.find(
        (item) =>
          Number(
            item.setting_id
          ) ===
          Number(settingId)
      );

    const original =
      originalConfig
        .additional_settings.find(
          (item) =>
            Number(
              item.setting_id
            ) ===
            Number(settingId)
        );

    if (
      !current ||
      !original
    ) {
      return false;
    }

    return (
      String(
        current.setting_value
      ) !==
        String(
          original.setting_value
        ) ||
      String(
        current.description
      ) !==
        String(
          original.description
        ) ||
      Boolean(
        current.is_active
      ) !==
        Boolean(
          original.is_active
        )
    );
  };

  const saveSubscription =
    async () => {
      const gst =
        Number(
          config.subscription
            .gst_percentage
        );

      if (
        !Number.isFinite(gst) ||
        gst < 0 ||
        gst > 100
      ) {
        showFeedback(
          "danger",
          "Subscription GST percentage must be between 0 and 100."
        );
        return;
      }

      const result =
        await dispatch(
          updateSubscriptionBillingConfigThunk({
            gst_percentage: gst,
          })
        );

      if (
        updateSubscriptionBillingConfigThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to update subscription GST"
        );
        return;
      }

      const savedValue =
        result.payload?.data
          ?.gst_percentage ??
        gst;

      setConfig((prev) => ({
        ...prev,
        subscription: {
          gst_percentage:
            savedValue,
        },
      }));

      setOriginalConfig(
        (prev) => ({
          ...prev,
          subscription: {
            gst_percentage:
              savedValue,
          },
        })
      );

      showFeedback(
        "success",
        result.payload?.message ||
          "Subscription GST updated successfully."
      );
    };

  const saveScheduler =
    async () => {
      const cronTime =
        String(
          config.scheduler
            .subscription_cron_time ||
            ""
        ).trim();

      if (
        !isValidTime(cronTime)
      ) {
        showFeedback(
          "danger",
          "Scheduler time must be in valid HH:MM format."
        );
        return;
      }

      const result =
        await dispatch(
          updateBillingSchedulerThunk({
            subscription_cron_time:
              cronTime,
          })
        );

      if (
        updateBillingSchedulerThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to update subscription scheduler"
        );
        return;
      }

      const savedValue =
        result.payload?.data
          ?.subscription_cron_time ??
        cronTime;

      setConfig((prev) => ({
        ...prev,
        scheduler: {
          subscription_cron_time:
            savedValue,
        },
      }));

      setOriginalConfig(
        (prev) => ({
          ...prev,
          scheduler: {
            subscription_cron_time:
              savedValue,
          },
        })
      );

      showFeedback(
        "success",
        result.payload?.message ||
          "Subscription scheduler updated successfully."
      );
    };

  const saveInvoice =
    async () => {
      const prefix =
        String(
          config.invoice
            .invoice_prefix ||
            ""
        )
          .trim()
          .toUpperCase();

      if (
        !isValidPrefix(prefix)
      ) {
        showFeedback(
          "danger",
          "Invoice prefix is required and may contain only letters, numbers, hyphen and underscore."
        );
        return;
      }

      const result =
        await dispatch(
          updateInvoiceBillingConfigThunk({
            invoice_prefix: prefix,
          })
        );

      if (
        updateInvoiceBillingConfigThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to update invoice prefix"
        );
        return;
      }

      const savedValue =
        result.payload?.data
          ?.invoice_prefix ??
        prefix;

      setConfig((prev) => ({
        ...prev,
        invoice: {
          invoice_prefix:
            savedValue,
        },
      }));

      setOriginalConfig(
        (prev) => ({
          ...prev,
          invoice: {
            invoice_prefix:
              savedValue,
          },
        })
      );

      showFeedback(
        "success",
        result.payload?.message ||
          "Invoice prefix updated successfully."
      );
    };

  const saveManualTax =
    async (category) => {
      const item =
        config.manual_tax?.[
          category
        ];

      if (!item) return;

      let payload = {
        tax_type:
          item.tax_type,
      };

      if (
        item.tax_type ===
        "GST"
      ) {
        const taxValue =
          Number(
            item.tax_value
          );

        if (
          ![
            "percentage",
            "amount",
          ].includes(
            item.tax_mode
          )
        ) {
          showFeedback(
            "danger",
            "Tax mode must be Percentage or Amount."
          );
          return;
        }

        if (
          !Number.isFinite(
            taxValue
          ) ||
          taxValue <= 0
        ) {
          showFeedback(
            "danger",
            "GST value must be greater than 0."
          );
          return;
        }

        if (
          item.tax_mode ===
            "percentage" &&
          taxValue > 100
        ) {
          showFeedback(
            "danger",
            "GST percentage cannot be greater than 100."
          );
          return;
        }

        payload = {
          tax_type: "GST",
          tax_mode:
            item.tax_mode,
          tax_value:
            taxValue,
        };
      }

      const result =
        await dispatch(
          updateManualTaxBillingConfigThunk({
            category,
            payload,
          })
        );

      if (
        updateManualTaxBillingConfigThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to update manual bill tax configuration"
        );
        return;
      }

      const saved =
        result.payload?.data ||
        payload;

      const normalized = {
        category,

        tax_type:
          saved.tax_type ===
          "Non-GST"
            ? "Non-GST"
            : "GST",

        tax_mode:
          saved.tax_type ===
          "Non-GST"
            ? "percentage"
            : saved.tax_mode ===
              "amount"
            ? "amount"
            : "percentage",

        tax_value:
          saved.tax_type ===
          "Non-GST"
            ? 0
            : saved.tax_value,
      };

      setConfig((prev) => ({
        ...prev,

        manual_tax: {
          ...prev.manual_tax,
          [category]:
            normalized,
        },
      }));

      setOriginalConfig(
        (prev) => ({
          ...prev,

          manual_tax: {
            ...prev.manual_tax,
            [category]:
              normalized,
          },
        })
      );

      showFeedback(
        "success",
        result.payload?.message ||
          "Manual bill tax configuration updated successfully."
      );
    };

  const handleAddSetting =
    async () => {
      const settingKey =
        String(
          newSetting.setting_key ||
            ""
        )
          .trim()
          .toLowerCase();

      const settingValue =
        String(
          newSetting.setting_value ||
            ""
        ).trim();

      const description =
        String(
          newSetting.description ||
            ""
        ).trim();

      if (
        !isValidSettingKey(
          settingKey
        )
      ) {
        showFeedback(
          "danger",
          "Setting key must start with a lowercase letter and contain only lowercase letters, numbers and underscores."
        );
        return;
      }

      if (!settingValue) {
        showFeedback(
          "danger",
          "Setting value is required."
        );
        return;
      }

      if (
        settingValue.length >
        255
      ) {
        showFeedback(
          "danger",
          "Setting value cannot exceed 255 characters."
        );
        return;
      }

      if (
        description.length >
        255
      ) {
        showFeedback(
          "danger",
          "Description cannot exceed 255 characters."
        );
        return;
      }

      const result =
        await dispatch(
          addBillingSettingThunk({
            setting_key:
              settingKey,
            setting_value:
              settingValue,
            description,
            is_active:
              Boolean(
                newSetting.is_active
              ),
          })
        );

      if (
        addBillingSettingThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to add setting"
        );
        return;
      }

      const created =
        result.payload?.data;

      if (created) {
        setConfig((prev) => ({
          ...prev,
          additional_settings: [
            ...prev.additional_settings,
            created,
          ].sort((a, b) =>
            String(
              a.setting_key
            ).localeCompare(
              String(
                b.setting_key
              )
            )
          ),
        }));

        setOriginalConfig(
          (prev) => ({
            ...prev,
            additional_settings: [
              ...prev.additional_settings,
              created,
            ].sort((a, b) =>
              String(
                a.setting_key
              ).localeCompare(
                String(
                  b.setting_key
                )
              )
            ),
          })
        );
      }

      setNewSetting(
        EMPTY_NEW_SETTING
      );

      setShowAddSetting(false);

      showFeedback(
        "success",
        result.payload?.message ||
          "Setting added successfully."
      );
    };

  const saveAdditionalSetting =
    async (setting) => {
      const value =
        String(
          setting.setting_value ??
            ""
        ).trim();

      const description =
        String(
          setting.description ??
            ""
        ).trim();

      if (!value) {
        showFeedback(
          "danger",
          `Value is required for ${setting.setting_key}.`
        );
        return;
      }

      if (value.length > 255) {
        showFeedback(
          "danger",
          "Setting value cannot exceed 255 characters."
        );
        return;
      }

      if (
        description.length >
        255
      ) {
        showFeedback(
          "danger",
          "Description cannot exceed 255 characters."
        );
        return;
      }

      const result =
        await dispatch(
          updateBillingSettingThunk({
            settingId:
              setting.setting_id,

            payload: {
              setting_value:
                value,
              description,
              is_active:
                Boolean(
                  setting.is_active
                ),
            },
          })
        );

      if (
        updateBillingSettingThunk
          .rejected.match(result)
      ) {
        showFeedback(
          "danger",
          result.payload ||
            "Failed to update setting"
        );
        return;
      }

      const updated =
        result.payload?.data;

      if (updated) {
        setConfig((prev) => ({
          ...prev,
          additional_settings:
            prev.additional_settings.map(
              (item) =>
                Number(
                  item.setting_id
                ) ===
                Number(
                  updated.setting_id
                )
                  ? updated
                  : item
            ),
        }));

        setOriginalConfig(
          (prev) => ({
            ...prev,
            additional_settings:
              prev.additional_settings.map(
                (item) =>
                  Number(
                    item.setting_id
                  ) ===
                  Number(
                    updated.setting_id
                  )
                    ? updated
                    : item
              ),
          })
        );
      }

      showFeedback(
        "success",
        result.payload?.message ||
          "Setting updated successfully."
      );
    };

  return (
    <div className="gs-page bc-page">
      <div className="bc-header">
        <div>
          <h3 className="gs-title">
            Billing Configuration
          </h3>

          <div className="gs-subtitle">
            Manage subscription GST,
            scheduler, invoice prefix
            and manual billing rules.
          </div>
        </div>

        <Button
          variant="light"
          className="hca-refresh-btn"
          onClick={
            loadConfiguration
          }
          disabled={
            billingConfigurationLoading
          }
        >
          <FiRefreshCw
            size={15}
          />
          Refresh
        </Button>
      </div>

      {feedback && (
        <Alert
          variant={
            feedback.type
          }
          dismissible
          onClose={() =>
            setFeedback(null)
          }
          className="bc-alert"
        >
          {feedback.message}
        </Alert>
      )}

      {billingConfigurationLoading ? (
        <Card className="gs-tabs-card">
          <div className="gs-loading">
            <Spinner
              animation="border"
              size="sm"
            />
            Loading billing
            configuration...
          </div>
        </Card>
      ) : (
        <>
          <Row className="g-3 bc-top-row">
            <Col
              xl={4}
              lg={4}
              md={6}
              xs={12}
            >
              <Card className="bc-config-card">
                <Card.Body>
                  <div className="bc-card-head">
                    <div className="sg-header-icon">
                      <FiPercent
                        size={18}
                      />
                    </div>

                    <div>
                      <div className="bc-card-title">
                        Subscription Settings
                      </div>

                      <div className="bc-card-subtitle">
                        GST used only for subscription billing.
                      </div>
                    </div>
                  </div>

                  <div className="bc-card-content">
                    <Form.Group>
                      <Form.Label className="gs-label">
                        GST Percentage
                      </Form.Label>

                      <div className="input-group bc-input-group">
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="gs-control"
                          value={
                            config
                              .subscription
                              .gst_percentage
                          }
                          onChange={(
                            e
                          ) =>
                            updateSection(
                              "subscription",
                              "gst_percentage",
                              e.target
                                .value
                            )
                          }
                        />

                        <span className="input-group-text">
                          %
                        </span>
                      </div>

                      <div className="gs-help">
                        Allowed range:
                        0 to 100.
                      </div>
                    </Form.Group>
                  </div>

                  <div className="bc-card-action">
                    <Button
                      className="gs-primary-btn bc-save-btn"
                      onClick={
                        saveSubscription
                      }
                      disabled={
                        savingSubscriptionConfig ||
                        !subscriptionDirty
                      }
                    >
                      {savingSubscriptionConfig ? (
                        <Spinner
                          animation="border"
                          size="sm"
                        />
                      ) : (
                        <FiSave
                          size={15}
                        />
                      )}

                      Save GST
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xl={4}
              lg={4}
              md={6}
              xs={12}
            >
              <Card className="bc-config-card">
                <Card.Body>
                  <div className="bc-card-head">
                    <div className="sg-header-icon">
                      <FiClock
                        size={18}
                      />
                    </div>

                    <div>
                      <div className="bc-card-title">
                        Subscription Scheduler
                      </div>

                      <div className="bc-card-subtitle">
                        Daily subscription bill generation time.
                      </div>
                    </div>
                  </div>

                  <div className="bc-card-content">
                    <Form.Group>
                      <Form.Label className="gs-label">
                        Cron Time
                      </Form.Label>

                      <Form.Control
                        type="time"
                        className="gs-control"
                        value={
                          config
                            .scheduler
                            .subscription_cron_time
                        }
                        onChange={(
                          e
                        ) =>
                          updateSection(
                            "scheduler",
                            "subscription_cron_time",
                            e.target
                              .value
                          )
                        }
                      />

                      <div className="gs-help">
                        India time
                        (Asia/Kolkata).
                        Restart Node/PM2
                        after changing.
                      </div>
                    </Form.Group>
                  </div>

                  <div className="bc-card-action">
                    <Button
                      className="gs-primary-btn bc-save-btn"
                      onClick={
                        saveScheduler
                      }
                      disabled={
                        savingSchedulerConfig ||
                        !schedulerDirty
                      }
                    >
                      {savingSchedulerConfig ? (
                        <Spinner
                          animation="border"
                          size="sm"
                        />
                      ) : (
                        <FiSave
                          size={15}
                        />
                      )}

                      Save Time
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col
              xl={4}
              lg={4}
              md={6}
              xs={12}
            >
              <Card className="bc-config-card">
                <Card.Body>
                  <div className="bc-card-head">
                    <div className="sg-header-icon">
                      <FiFileText
                        size={18}
                      />
                    </div>

                    <div>
                      <div className="bc-card-title">
                        Invoice Settings
                      </div>

                      <div className="bc-card-subtitle">
                        Prefix used while generating invoice numbers.
                      </div>
                    </div>
                  </div>

                  <div className="bc-card-content">
                    <Form.Group>
                      <Form.Label className="gs-label">
                        Invoice Prefix
                      </Form.Label>

                      <Form.Control
                        type="text"
                        maxLength={20}
                        className="gs-control"
                        value={
                          config.invoice
                            .invoice_prefix
                        }
                        onChange={(
                          e
                        ) =>
                          updateSection(
                            "invoice",
                            "invoice_prefix",
                            e.target
                              .value
                              .toUpperCase()
                          )
                        }
                        placeholder="INV"
                      />

                      <div className="gs-help">
                        Letters, numbers,
                        hyphen and underscore
                        only.
                      </div>
                    </Form.Group>
                  </div>

                  <div className="bc-card-action">
                    <Button
                      className="gs-primary-btn bc-save-btn"
                      onClick={
                        saveInvoice
                      }
                      disabled={
                        savingInvoiceConfig ||
                        !invoiceDirty
                      }
                    >
                      {savingInvoiceConfig ? (
                        <Spinner
                          animation="border"
                          size="sm"
                        />
                      ) : (
                        <FiSave
                          size={15}
                        />
                      )}

                      Save Prefix
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="bc-section-card">
            <Card.Body>
              <div className="bc-section-header">
                <div className="bc-section-heading">
                  <div className="sg-header-icon">
                    <FiSettings
                      size={18}
                    />
                  </div>

                  <div>
                    <div className="bc-section-title">
                      Manual Bill Tax Configuration
                    </div>

                    <div className="bc-section-subtitle">
                      Fixed tax rule automatically applied when a manual bill is created.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bc-rule-note">
                <FiInfo
                  size={17}
                />

                <div>
                  <strong>
                    Tax rule
                  </strong>

                  <span>
                    For Non-GST, GST mode and value are automatically set to zero. For GST, choose either Percentage or Amount, never both.
                  </span>
                </div>
              </div>

              <div className="bc-table-wrap">
                <Table
                  responsive
                  className="bc-table mb-0"
                >
                  <thead>
                    <tr>
                      <th>
                        Category
                      </th>
                      <th>
                        Tax Type
                      </th>
                      <th>
                        GST Mode
                      </th>
                      <th>
                        Tax Value
                      </th>
                      <th className="bc-action-col">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {MANUAL_CATEGORIES.map(
                      ({
                        key,
                        label,
                      }) => {
                        const item =
                          config
                            .manual_tax?.[
                            key
                          ] || {};

                        const isNonGst =
                          item.tax_type ===
                          "Non-GST";

                        const isSaving =
                          Boolean(
                            savingManualTaxConfig?.[
                              key
                            ]
                          );

                        return (
                          <tr key={key}>
                            <td>
                              <div className="bc-category-name">
                                {label}
                              </div>
                            </td>

                            <td>
                              <Form.Select
                                className="gs-control bc-select"
                                value={
                                  item.tax_type ||
                                  "GST"
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateManualTax(
                                    key,
                                    "tax_type",
                                    e.target
                                      .value
                                  )
                                }
                              >
                                <option value="GST">
                                  GST
                                </option>

                                <option value="Non-GST">
                                  Non-GST
                                </option>
                              </Form.Select>
                            </td>

                            <td>
                              <Form.Select
                                className="gs-control bc-select"
                                value={
                                  item.tax_mode ||
                                  "percentage"
                                }
                                disabled={
                                  isNonGst
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateManualTax(
                                    key,
                                    "tax_mode",
                                    e.target
                                      .value
                                  )
                                }
                              >
                                <option value="percentage">
                                  Percentage
                                </option>

                                <option value="amount">
                                  Amount
                                </option>
                              </Form.Select>
                            </td>

                            <td>
                              <div className="input-group bc-input-group">
                                <span className="input-group-text">
                                  {isNonGst
                                    ? "-"
                                    : item.tax_mode ===
                                      "amount"
                                    ? "₹"
                                    : "%"}
                                </span>

                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="gs-control"
                                  disabled={
                                    isNonGst
                                  }
                                  value={
                                    isNonGst
                                      ? 0
                                      : item.tax_value ??
                                        ""
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    updateManualTax(
                                      key,
                                      "tax_value",
                                      e.target
                                        .value
                                    )
                                  }
                                  placeholder={
                                    item.tax_mode ===
                                    "amount"
                                      ? "Enter amount"
                                      : "Enter percentage"
                                  }
                                />
                              </div>

                              {!isNonGst && (
                                <div className="bc-field-help">
                                  {item.tax_mode ===
                                  "amount"
                                    ? "Fixed GST amount."
                                    : "Calculated on base amount."}
                                </div>
                              )}
                            </td>

                            <td className="bc-action-col">
                              <Button
                                className="gs-primary-btn bc-row-save"
                                onClick={() =>
                                  saveManualTax(
                                    key
                                  )
                                }
                                disabled={
                                  isSaving ||
                                  !isManualDirty(
                                    key
                                  )
                                }
                              >
                                {isSaving ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                  />
                                ) : (
                                  <FiSave
                                    size={15}
                                  />
                                )}

                                Save
                              </Button>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Card className="bc-section-card bc-additional-card">
            <Card.Body>
              <div className="bc-section-header">
                <div className="bc-section-heading">
                  <div className="sg-header-icon">
                    <FiSliders
                      size={18}
                    />
                  </div>

                  <div>
                    <div className="bc-section-title">
                      Additional Settings
                    </div>

                    <div className="bc-section-subtitle">
                      Add extra key/value settings when required. Existing keys are read-only and cannot be renamed.
                    </div>
                  </div>
                </div>

                <Button
                  className="gs-primary-btn bc-add-btn"
                  onClick={() => {
                    setNewSetting(
                      EMPTY_NEW_SETTING
                    );
                    setShowAddSetting(
                      true
                    );
                  }}
                >
                  <FiPlus size={16} />
                  Add Setting
                </Button>
              </div>

              {config
                .additional_settings
                .length === 0 ? (
                <div className="bc-empty">
                  <FiSliders
                    size={22}
                  />

                  <strong>
                    No additional settings
                  </strong>

                  <span>
                    Use Add Setting when you need a new custom configuration.
                  </span>
                </div>
              ) : (
                <div className="bc-table-wrap">
                  <Table
                    responsive
                    className="bc-table bc-additional-table mb-0"
                  >
                    <thead>
                      <tr>
                        <th>
                          Setting Key
                        </th>
                        <th>
                          Value
                        </th>
                        <th>
                          Description
                        </th>
                        <th>
                          Active
                        </th>
                        <th className="bc-action-col">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {config.additional_settings.map(
                        (setting) => {
                          const isSaving =
                            Boolean(
                              savingAdditionalSettings?.[
                                setting
                                  .setting_id
                              ]
                            );

                          return (
                            <tr
                              key={
                                setting.setting_id
                              }
                            >
                              <td>
                                <Form.Control
                                  className="gs-control bc-readonly-key"
                                  value={
                                    setting.setting_key
                                  }
                                  readOnly
                                />
                              </td>

                              <td>
                                <Form.Control
                                  className="gs-control"
                                  maxLength={255}
                                  value={
                                    setting.setting_value
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    updateAdditionalLocal(
                                      setting.setting_id,
                                      "setting_value",
                                      e.target
                                        .value
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <Form.Control
                                  className="gs-control"
                                  maxLength={255}
                                  value={
                                    setting.description
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    updateAdditionalLocal(
                                      setting.setting_id,
                                      "description",
                                      e.target
                                        .value
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <Form.Check
                                  type="switch"
                                  className="bc-switch"
                                  checked={
                                    Boolean(
                                      setting.is_active
                                    )
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    updateAdditionalLocal(
                                      setting.setting_id,
                                      "is_active",
                                      e.target
                                        .checked
                                    )
                                  }
                                />
                              </td>

                              <td className="bc-action-col">
                                <Button
                                  className="gs-primary-btn bc-row-save"
                                  onClick={() =>
                                    saveAdditionalSetting(
                                      setting
                                    )
                                  }
                                  disabled={
                                    isSaving ||
                                    !isAdditionalDirty(
                                      setting.setting_id
                                    )
                                  }
                                >
                                  {isSaving ? (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                    />
                                  ) : (
                                    <FiSave
                                      size={15}
                                    />
                                  )}

                                  Save
                                </Button>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      <Modal
        show={showAddSetting}
        onHide={() =>
          !addingBillingSetting &&
          setShowAddSetting(false)
        }
        centered
        className="bc-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              Add New Setting
            </Modal.Title>

            <div className="bc-modal-subtitle">
              The key is permanent after creation. You can update its value, description and active status later.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="gs-label">
              Setting Key
              <span className="text-danger">
                *
              </span>
            </Form.Label>

            <Form.Control
              className="gs-control"
              maxLength={100}
              value={
                newSetting.setting_key
              }
              onChange={(e) =>
                setNewSetting(
                  (prev) => ({
                    ...prev,
                    setting_key:
                      e.target.value
                        .toLowerCase()
                        .replace(
                          /\s+/g,
                          "_"
                        ),
                  })
                )
              }
              placeholder="example_setting_key"
            />

            <div className="gs-help">
              Lowercase letters,
              numbers and underscore
              only.
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="gs-label">
              Setting Value
              <span className="text-danger">
                *
              </span>
            </Form.Label>

            <Form.Control
              className="gs-control"
              maxLength={255}
              value={
                newSetting.setting_value
              }
              onChange={(e) =>
                setNewSetting(
                  (prev) => ({
                    ...prev,
                    setting_value:
                      e.target.value,
                  })
                )
              }
              placeholder="Enter value"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="gs-label">
              Description
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              maxLength={255}
              className="bc-textarea"
              value={
                newSetting.description
              }
              onChange={(e) =>
                setNewSetting(
                  (prev) => ({
                    ...prev,
                    description:
                      e.target.value,
                  })
                )
              }
              placeholder="What is this setting used for?"
            />
          </Form.Group>

          <div className="bc-active-row">
            <div>
              <strong>
                Active
              </strong>

              <span>
                Enable this setting immediately after creation.
              </span>
            </div>

            <Form.Check
              type="switch"
              className="bc-switch"
              checked={
                Boolean(
                  newSetting.is_active
                )
              }
              onChange={(e) =>
                setNewSetting(
                  (prev) => ({
                    ...prev,
                    is_active:
                      e.target
                        .checked,
                  })
                )
              }
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            className="hca-secondary-btn"
            disabled={
              addingBillingSetting
            }
            onClick={() =>
              setShowAddSetting(
                false
              )
            }
          >
            Cancel
          </Button>

          <Button
            className="gs-primary-btn bc-modal-save"
            disabled={
              addingBillingSetting
            }
            onClick={
              handleAddSetting
            }
          >
            {addingBillingSetting ? (
              <Spinner
                animation="border"
                size="sm"
              />
            ) : (
              <FiPlus
                size={16}
              />
            )}

            Add Setting
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BillingConfigurationPage;
